import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/utils/cn';
import { BrandSpinner } from './brand-spinner';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'danger-outline';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      isLoading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;

    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-md font-medium',
          'transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-offset-2',
          'disabled:opacity-60 disabled:cursor-not-allowed',
          // Variants
          variant === 'primary' && [
            'bg-primary-accent !text-primary-black',
            'hover:opacity-90 active:opacity-80',
            'focus:ring-primary-accent',
          ],
          variant === 'secondary' && [
            'bg-grey border border-primary-white',
            'text-primary-white',
            'hover:bg-neutral-800 active:bg-neutral-900',
            'focus:ring-primary-white',
          ],
          variant === 'outline' && [
            'bg-transparent border',
            'border-border-default',
            'text-foreground-primary',
            'hover:bg-neutral-50 dark:hover:bg-neutral-900',
            'active:bg-neutral-100 dark:active:bg-neutral-800',
            'focus:ring-primary-accent',
          ],
          variant === 'ghost' && [
            'bg-transparent',
            'text-foreground-primary',
            'hover:bg-neutral-100 dark:hover:bg-neutral-800',
            'active:bg-neutral-200 dark:active:bg-neutral-700',
            'focus:ring-primary-accent',
          ],
          variant === 'danger' && [
            'bg-error text-primary-white',
            'hover:opacity-90 active:opacity-80',
            'focus:ring-error',
          ],
          variant === 'danger-outline' && [
            'bg-transparent border border-error',
            'text-error',
            'hover:bg-error/10 active:bg-error/20',
            'focus:ring-error',
          ],
          // Sizes - using fixed heights for consistency
          size === 'sm' && 'h-10 px-4 text-body-small',
          size === 'md' && 'h-12 px-6 text-body-medium',
          size === 'lg' && 'h-12 px-6 text-body-medium',
          // Full width
          fullWidth && 'w-full',
          className
        )}
        disabled={isDisabled}
        {...props}
      >
        {isLoading ? (
          <BrandSpinner size="sm" />
        ) : (
          <>
            {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
